/**
 * Vender al peso: de gramos a kilos, y de dinero a peso.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA QUE ESTO RESUELVE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Un producto que se vende por kilo entraba al carrito como **un kilo**, y para
 * vender 400 gramos había que escribir `0,4` — dividir de cabeza, con gente
 * esperando. El dueño lo dijo así: «solo sale el Kg y ya».
 *
 * Y falta la mitad más importante. En una bodega nadie pide «0,4 kilos de
 * queso»: pide **«dame dos dólares de queso»**. Esa cuenta —cuánto queso son dos
 * dólares— la estaba haciendo el cajero a mano, y es la que más se equivoca
 * porque se hace rápido y con el cliente delante.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL IMPUESTO VA DENTRO DEL DINERO QUE PIDE EL CLIENTE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Quien dice «dos dólares de queso» está diciendo lo que va a sacar del
 * bolsillo, no una base imponible. Si el impuesto va por fuera y se calculara
 * contra el precio pelado, le saldría más queso del que pagó y al cobrar
 * aparecerían 2,32 — el cliente diría que pidió dos.
 *
 * Por eso `precioFinalPorUnidad` suma el impuesto cuando va por fuera. Es la
 * misma separación que hace `calcularTotales` en `stores/carrito.ts` y
 * `split_tax()` en `core/money.py`.
 */

/** Lo que hace falta saber de una unidad que se vende partida. */
export type UnidadMenuda = {
  /** Cómo se escribe la unidad de venta: «kg», «L». */
  corta: string;
  /** La unidad chica en la que la gente habla: «g», «ml». */
  menuda: string;
  /** Cuántas menudas caben en una de venta. */
  porUnidad: number;
};

/**
 * ⚠️ Solo peso y volumen, y a propósito.
 *
 * Los metros de tela también se venden partidos, pero nadie pide «300
 * milímetros de tela»: pide metro y medio. Meter aquí el metro llenaría la
 * pantalla de una unidad que nadie usa en el mostrador.
 *
 * Las unidades enteras (`unit`, `box`, `pack`) no entran nunca: media lata de
 * refresco no existe.
 */
const MENUDAS: Record<string, UnidadMenuda> = {
  kg: { corta: "kg", menuda: "g", porUnidad: 1000 },
  l: { corta: "L", menuda: "ml", porUnidad: 1000 },
};

export function unidadDePeso(unit: string | null | undefined): UnidadMenuda | null {
  return MENUDAS[(unit ?? "").toLowerCase()] ?? null;
}

/**
 * Cómo se escribe una unidad de venta al lado de una casilla.
 *
 * ⚠️ DEVUELVE VACÍO SOLO PARA `unit`, y ese es el punto.
 *
 * Un producto que se vende POR GRAMO no necesita conversión —«1» ya es un
 * gramo— pero sí necesita que se vea el «g». Sin él, la casilla dice «1» igual
 * que la del pan, y no hay forma de saber si eso es un gramo de pollo o un
 * pollo entero. Es la mitad del fallo que el dueño encontró armando una arepa:
 * el queso llevaba un kilo y el pollo un gramo, y las dos casillas decían «1».
 *
 * Para lo que va por piezas no se pone nada: «1 unidad» al lado de un pan es
 * ruido, y esa es la mayoría del catálogo.
 */
const COMO_SE_ESCRIBE: Record<string, string> = {
  kg: "kg",
  g: "g",
  l: "L",
  ml: "ml",
  m: "m",
  box: "cajas",
  pack: "paq.",
  hour: "h",
};

export function etiquetaDeUnidad(unit: string | null | undefined): string {
  return COMO_SE_ESCRIBE[(unit ?? "").toLowerCase()] ?? "";
}

// =============================================================================
//  CÓMO SE LLAMA CADA UNIDAD CUANDO HAY QUE ESCRIBIR UNA FRASE
// =============================================================================

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LO VIO EL DUEÑO EN LA FICHA DE UN PRODUCTO AL PESO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eligió «Kilogramo» en «Se vende por» y todo lo de abajo siguió hablando de
 * unidades: «Lo compro por unidad», «Cada unidad te cuesta», «¿Cuántas unidades
 * trae?». Sus palabras: «mira cómo está guardado y cómo sale al poner bulto».
 *
 * Y no es cosmético. «¿Cuántas unidades trae el bulto?» con un producto que se
 * vende por kilo tiene DOS lecturas —¿cuántos paquetes, o cuántos kilos?— y las
 * dos son razonables. Quien conteste la que no es se lleva un costo por unidad
 * mal dividido, que es el número del que sale toda la ganancia.
 *
 * Con «¿Cuántos kilos trae?» no hay nada que interpretar.
 *
 * El género importa: «¿Cuántas cajas trae?» y «¿Cuántos kilos trae?». Sin él,
 * media pantalla queda mal escrita, y una pantalla mal escrita se lee como
 * descuidada — que es justo lo contrario de lo que tiene que transmitir la
 * ficha donde se decide cuánto gana el negocio.
 */
export type PalabrasDeUnidad = {
  /** «kilo», «caja», «unidad». */
  singular: string;
  /** «kilos», «cajas», «unidades». */
  plural: string;
  /** Para escribir «¿Cuántos kilos?» o «¿Cuántas cajas?». */
  femenino: boolean;
};

const PALABRAS: Record<string, PalabrasDeUnidad> = {
  unit: { singular: "unidad", plural: "unidades", femenino: true },
  kg: { singular: "kilo", plural: "kilos", femenino: false },
  g: { singular: "gramo", plural: "gramos", femenino: false },
  l: { singular: "litro", plural: "litros", femenino: false },
  ml: { singular: "mililitro", plural: "mililitros", femenino: false },
  m: { singular: "metro", plural: "metros", femenino: false },
  box: { singular: "caja", plural: "cajas", femenino: true },
  pack: { singular: "paquete", plural: "paquetes", femenino: false },
  hour: { singular: "hora", plural: "horas", femenino: true },
};

/**
 * Cómo se llama esta unidad en una frase.
 *
 * Lo desconocido cae en «unidad», que es lo que era antes para todo: si mañana
 * se añade una unidad nueva y a alguien se le olvida escribirla aquí, la
 * pantalla sigue leyéndose bien en vez de quedarse con un hueco.
 */
export function palabrasDeUnidad(unit: string | null | undefined): PalabrasDeUnidad {
  return PALABRAS[(unit ?? "").toLowerCase()] ?? PALABRAS.unit!;
}

/** «¿Cuántos kilos…?» / «¿Cuántas cajas…?» */
export function cuantos(unit: string | null | undefined): string {
  return palabrasDeUnidad(unit).femenino ? "Cuántas" : "Cuántos";
}

/**
 * Cuánto poner de un ingrediente recién añadido a una receta.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ «1» PARA TODO ERA UN DISPARATE EN LOS DOS SENTIDOS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El dueño lo vio armando una arepa de queso:
 *
 *   · Queso por KILO → «1» era **un kilo de queso** en una arepa.
 *   · Pollo por GRAMO → «1» era **un gramo de pollo**.
 *   · Pan por unidad → «1» era un pan, que sí estaba bien.
 *
 * Y no avisaba de nada: la cuenta cuadraba, solo que con la cantidad
 * equivocada. El costo salía disparatado y con él la ganancia, que es el único
 * número por el que existe esa pantalla.
 *
 * Cien gramos —o cien mililitros— es una cantidad de receta de verdad, y sobre
 * todo es OBVIAMENTE editable: nadie la da por buena sin mirarla, que es lo que
 * sí pasaba con «1». Para lo que va por piezas, uno sigue siendo lo correcto.
 *
 * Devuelve el número en la UNIDAD DE VENTA de la pieza, que es como se guarda.
 */
export function cantidadInicialEnReceta(item: {
  unit?: string | null;
  allow_decimal_qty?: boolean | null;
}): string {
  const u = (item.unit ?? "").toLowerCase();

  // Se vende por kilo o por litro: cien gramos son 0,1 de la unidad de venta.
  if (seVendeAlPeso(item)) return "0.1";

  // Se vende ya en la unidad chica: cien son cien.
  if (u === "g" || u === "ml") return "100";

  return "1";
}

/**
 * ¿De este producto hay que preguntar CUÁNTO antes de meterlo al carrito?
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ NO ES LO MISMO QUE `seVendeAlPeso`, Y LA DIFERENCIA IMPORTA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `seVendeAlPeso` responde «¿esto se pide en gramos?» — y solo el kilo y el
 * litro tienen una unidad chica que la gente use. Nadie pide «300 milímetros de
 * tela».
 *
 * Esta responde otra cosa: «¿esto se vende partido?». Y ahí entran también los
 * metros, los gramos y los mililitros.
 *
 * Estaban juntas, y por eso un cable en metros entraba al carrito como **un
 * metro** y había que corregir la cantidad a mano en la línea. Se perdía lo que
 * más sirve de la franja, que no es la casilla de los gramos: es la del dinero.
 *
 *     «Dame tres dólares de cable»
 *
 * Eso en una ferretería se dice todos los días, y sin la franja el cajero
 * divide de cabeza —el metro a 1,20, tres dólares son dos metros y medio— con
 * gente esperando. Es exactamente la cuenta que esta pantalla vino a quitar,
 * solo que para el queso sí y para el cable no.
 *
 * ⚠️ Sigue haciendo falta que el producto admita decimales. Un saco de arroz de
 * 1 kg tiene `unit: kg` y se vende entero: ahí preguntar cuánto es estorbar.
 */
export function sePreguntaCuanto(item: {
  unit?: string | null;
  allow_decimal_qty?: boolean | null;
}): boolean {
  if (!item.allow_decimal_qty) return false;
  return SE_PARTEN.has((item.unit ?? "").toLowerCase());
}

/**
 * Las unidades que se venden partidas.
 *
 * ⚠️ Tiene que decir lo mismo que `SE_PARTEN` en la ficha del producto y que la
 * lista de `allow_decimal_qty` del importador. Son los tres sitios donde se
 * decide lo mismo, y el día que no coincidan, un producto se comportará
 * distinto según por dónde se creó.
 */
const SE_PARTEN = new Set(["kg", "l", "g", "ml", "m"]);

/** ¿Este producto se vende al peso o por volumen? */
export function seVendeAlPeso(item: {
  unit?: string | null;
  allow_decimal_qty?: boolean | null;
}): boolean {
  /*
    Hacen falta LAS DOS cosas.

    `unit: kg` sin decimales es un producto que se vende por kilos enteros —un
    saco de arroz de 1 kg, por ejemplo—, y ahí preguntar gramos sería estorbar.
    Quien quiere vender queso partido enciende «permite decimales», que ya
    existe en el catálogo.
  */
  return Boolean(item.allow_decimal_qty) && unidadDePeso(item.unit) !== null;
}

/**
 * Lo que de verdad paga el cliente por una unidad de venta.
 *
 * Es contra este número contra el que se convierte el dinero, no contra el
 * precio pelado. Ver la nota del encabezado.
 */
export function precioFinalPorUnidad(
  precio: number,
  tasaImpuesto = 0,
  impuestoIncluido = true,
): number {
  if (!Number.isFinite(precio) || precio <= 0) return 0;
  if (impuestoIncluido || !Number.isFinite(tasaImpuesto) || tasaImpuesto <= 0) {
    return precio;
  }
  return precio * (1 + tasaImpuesto);
}

/**
 * Redondeo a la milésima.
 *
 * ⚠️ Tres decimales porque es lo que guarda la base (`QUANTITY` es
 * `NUMERIC(14,3)`). En kilos, una milésima es **un gramo**: la misma precisión
 * que da una balanza de mostrador, así que no se pierde nada por el camino.
 *
 * Si aquí se redondeara a dos, el gramo más chico que se podría vender serían
 * diez, y «dame 5 gramos de azafrán» no se podría cobrar.
 */
function aMilesimas(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 1000) / 1000;
}

/** De gramos (o mililitros) a la cantidad que guarda la venta. */
export function cantidadDesdeMenuda(menuda: number, porUnidad: number): number {
  if (!Number.isFinite(menuda) || menuda <= 0 || porUnidad <= 0) return 0;
  return aMilesimas(menuda / porUnidad);
}

/** Y de vuelta, para enseñarlo. */
export function menudaDesdeCantidad(cantidad: number, porUnidad: number): number {
  if (!Number.isFinite(cantidad) || cantidad <= 0 || porUnidad <= 0) return 0;
  return Math.round(cantidad * porUnidad);
}

/**
 * Cuánto producto son X de dinero.
 *
 * ⚠️ Con precio cero devuelve cero, no infinito. Un producto sin precio existe
 * —los que se suben en masa y quedan pendientes de precio—, y sin este corte la
 * pantalla pediría cobrar una cantidad infinita de queso.
 */
export function cantidadDesdeDinero(dinero: number, precioFinal: number): number {
  if (!Number.isFinite(dinero) || dinero <= 0) return 0;
  if (!Number.isFinite(precioFinal) || precioFinal <= 0) return 0;
  return aMilesimas(dinero / precioFinal);
}

/**
 * Cómo se escribe una cantidad al peso para que se lea como se dice.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ A PARTIR DE UN KILO SE DICE EN KILOS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Lo dijo el dueño: «pero a partir de mil ps ya sí se usa kg». Y es exactamente
 * como se habla en el mostrador — por debajo del kilo se pide en gramos
 * («quinientos de queso») y a partir de ahí en kilos («kilo y medio»). Nadie
 * dice «mil quinientos gramos de carne».
 *
 * Escribir 1500 g obliga a dividir de cabeza para saber si es más o menos de un
 * kilo, que es justo la cuenta que esta pantalla vino a quitar.
 *
 * Los decimales se recortan: 1 kg y no 1,000 kg; 1,5 kg y no 1,500 kg.
 */
export function comoSeDice(cantidad: number, unidad: UnidadMenuda): string {
  if (!Number.isFinite(cantidad) || cantidad <= 0) return `0 ${unidad.menuda}`;

  // Por debajo de una unidad de venta, en la chica: «500 g».
  if (cantidad < 1) {
    return `${menudaDesdeCantidad(cantidad, unidad.porUnidad)} ${unidad.menuda}`;
  }

  // De ahí para arriba, en la de venta: «1 kg», «1,5 kg», «1,25 kg».
  const texto = cantidad
    .toFixed(3)
    .replace(/0+$/, "")
    .replace(/\.$/, "")
    .replace(".", ",");
  return `${texto} ${unidad.corta}`;
}

/** Lo que va a costar esa cantidad. Es lo que se le enseña al cajero. */
export function dineroDesdeCantidad(cantidad: number, precioFinal: number): number {
  if (!Number.isFinite(cantidad) || cantidad <= 0) return 0;
  if (!Number.isFinite(precioFinal) || precioFinal <= 0) return 0;
  return Math.round(cantidad * precioFinal * 100) / 100;
}
